import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useReactToPrint } from 'react-to-print';
import QRCode from 'qrcode';
import { useOutlet } from '../../context/OutletContext';
import { useBilling } from '../../admin/context/BillingContext';
import './BillPrinting.css';

const BillPrinting = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const componentRef = useRef();
  const { selectedOutlet: outletInfo } = useOutlet();
  const { gstSettings, calculateGST } = useBilling();

  const [order, setOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [billSettings, setBillSettings] = useState({
    showLogo: true,
    showGST: true,
    showFSSAI: true,
    showQRCode: true,
    showCustomerDetails: true
  });
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          setOrder(orderData);

          // Set customer details if available
          if (orderData.customerDetails) {
            setCustomerDetails(orderData.customerDetails);
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Generate UPI QR Code
  useEffect(() => {
    if (order && billSettings.showQRCode) {
      generateUPIQRCode();
    }
  }, [order, billSettings.showQRCode]);

  const generateUPIQRCode = async () => {
    try {
      const total = calculateTotal();
      const upiString = `upi://pay?pa=${outletInfo?.upiId || 'merchant@upi'}&pn=${encodeURIComponent(outletInfo?.name || 'Odisha Zaika')}&am=${total.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Order #${order?.orderNumber || order?.tokenNumber}`)}`;

      const qrDataUrl = await QRCode.toDataURL(upiString, {
        width: 200,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Bill-${order?.orderNumber || order?.tokenNumber}`,
    onAfterPrint: () => console.log('Bill printed successfully')
  });

  const handlePayment = async () => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'completed',
        paymentMethod,
        paymentStatus: 'paid',
        paidAt: Timestamp.now(),
        completedAt: Timestamp.now(),
        customerDetails: billSettings.showCustomerDetails ? customerDetails : null
      });
      alert('Payment successful! Order completed.');
      navigate('/waiter/order-line');
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Error processing payment. Please try again.');
    }
  };

  const saveCustomerDetails = async () => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        customerDetails,
        updatedAt: Timestamp.now()
      });
      setIsEditingCustomer(false);
      alert('Customer details updated!');
    } catch (error) {
      console.error('Error saving customer details:', error);
      alert('Error saving customer details');
    }
  };

  if (!order) {
    return (
      <div className="bill-loading">
        <div className="loading-spinner"></div>
        <p>Loading bill...</p>
      </div>
    );
  }

  const subtotal = order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const gstCalculation = calculateGST(subtotal);
  const total = gstCalculation.total;

  return (
    <div className="bill-printing-container">
      {/* Bill Customization Panel */}
      <div className="bill-settings-panel">
        <h3>Bill Settings</h3>

        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={billSettings.showLogo}
              onChange={(e) => setBillSettings({...billSettings, showLogo: e.target.checked})}
            />
            Print Your Logo
          </label>
        </div>

        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={billSettings.showGST}
              onChange={(e) => setBillSettings({...billSettings, showGST: e.target.checked})}
            />
            GST & FSSAI Number
          </label>
        </div>

        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={billSettings.showQRCode}
              onChange={(e) => setBillSettings({...billSettings, showQRCode: e.target.checked})}
            />
            UPI Payment QR Code
          </label>
        </div>

        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={billSettings.showCustomerDetails}
              onChange={(e) => setBillSettings({...billSettings, showCustomerDetails: e.target.checked})}
            />
            Create Bill Break-up
          </label>
        </div>

        {/* Customer Details Edit */}
        {billSettings.showCustomerDetails && (
          <div className="customer-details-edit">
            <h4>Customer Details</h4>
            {isEditingCustomer ? (
              <div className="edit-form">
                <input
                  type="text"
                  placeholder="Customer Name"
                  value={customerDetails.name}
                  onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={customerDetails.phone}
                  onChange={(e) => setCustomerDetails({...customerDetails, phone: e.target.value})}
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={customerDetails.email}
                  onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                />
                <div className="edit-actions">
                  <button onClick={saveCustomerDetails} className="btn-save">Save</button>
                  <button onClick={() => setIsEditingCustomer(false)} className="btn-cancel">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="view-details">
                <p><strong>Name:</strong> {customerDetails.name || 'Not provided'}</p>
                <p><strong>Phone:</strong> {customerDetails.phone || 'Not provided'}</p>
                {customerDetails.email && <p><strong>Email:</strong> {customerDetails.email}</p>}
                <button onClick={() => setIsEditingCustomer(true)} className="btn-edit">Edit Details</button>
              </div>
            )}
          </div>
        )}

        <div className="payment-method-selector">
          <h4>Payment Method</h4>
          <div className="payment-options">
            <button
              className={`payment-option ${paymentMethod === 'cash' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('cash')}
            >
              💵 Cash
            </button>
            <button
              className={`payment-option ${paymentMethod === 'card' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              💳 Card
            </button>
            <button
              className={`payment-option ${paymentMethod === 'upi' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('upi')}
            >
              📱 UPI
            </button>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={handlePrint} className="btn-print">
            🖨️ Print Bill
          </button>
          <button onClick={handlePayment} className="btn-complete">
            ✅ Complete Payment
          </button>
        </div>
      </div>

      {/* Printable Bill */}
      <div className="bill-preview">
        <div ref={componentRef} className="bill-document">
          {/* Header with Logo */}
          {billSettings.showLogo && outletInfo?.logo && (
            <div className="bill-logo">
              <img src={outletInfo.logo} alt={outletInfo.name} />
            </div>
          )}

          {/* Restaurant Info */}
          <div className="bill-header">
            <h1>{outletInfo?.name || 'Odisha Zaika'}</h1>
            <p className="bill-type">Retail Invoice</p>
            {billSettings.showGST && (
              <div className="tax-info">
                {gstSettings?.gstNumber && (
                  <p><strong>GST:</strong> {gstSettings.gstNumber}</p>
                )}
                {outletInfo?.fssaiNumber && (
                  <p><strong>FSSAI:</strong> {outletInfo.fssaiNumber}</p>
                )}
              </div>
            )}
            <p className="address">{outletInfo?.address || 'Bhubaneswar, Odisha'}</p>
            <p className="contact">📞 {outletInfo?.contactNumbers?.primary || '7873611363'}</p>
          </div>

          {/* Bill Details */}
          <div className="bill-meta">
            <div className="meta-row">
              <span><strong>Token no.</strong> {order.tokenNumber || '7'}</span>
              <span>{new Date(order.timestamp?.toDate ? order.timestamp.toDate() : new Date()).toLocaleString()}</span>
            </div>
            <div className="meta-row">
              <span><strong>Order:</strong> #{order.orderNumber}</span>
              <span><strong>Table:</strong> {order.tableNumber || 'N/A'}</span>
            </div>
          </div>

          {/* Customer Details */}
          {billSettings.showCustomerDetails && (customerDetails.name || customerDetails.phone) && (
            <div className="customer-info">
              <h3>Customer Details</h3>
              {customerDetails.name && <p><strong>Name:</strong> {customerDetails.name}</p>}
              {customerDetails.phone && <p><strong>Phone:</strong> {customerDetails.phone}</p>}
              {customerDetails.email && <p><strong>Email:</strong> {customerDetails.email}</p>}
            </div>
          )}

          {/* Items Table */}
          <table className="bill-items-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Amt</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <div className="item-name-section">
                      {idx + 1}. {item.name}
                      {item.customization && (
                        <span className="customization">{item.customization}</span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-right">₹{item.price.toFixed(2)}</td>
                  <td className="text-right">₹{(item.price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Bill Break-up */}
          <div className="bill-summary">
            <div className="summary-row">
              <span>Sub-total:</span>
              <span>₹{gstCalculation.subtotal.toFixed(2)}</span>
            </div>

            {gstSettings?.gstEnabled && billSettings.showGST && (
              <>
                {gstCalculation.cgst > 0 && (
                  <div className="summary-row">
                    <span>CGST ({gstSettings.cgst}%):</span>
                    <span>₹{gstCalculation.cgst.toFixed(2)}</span>
                  </div>
                )}
                {gstCalculation.sgst > 0 && (
                  <div className="summary-row">
                    <span>SGST ({gstSettings.sgst}%):</span>
                    <span>₹{gstCalculation.sgst.toFixed(2)}</span>
                  </div>
                )}
                {gstCalculation.igst > 0 && (
                  <div className="summary-row">
                    <span>IGST ({gstSettings.igst}%):</span>
                    <span>₹{gstCalculation.igst.toFixed(2)}</span>
                  </div>
                )}
              </>
            )}

            {order.discount > 0 && (
              <div className="summary-row discount">
                <span>Discount:</span>
                <span>₹{order.discount.toFixed(2)}</span>
              </div>
            )}

            <div className="summary-row grand-total">
              <span><strong>Grand Total:</strong></span>
              <span><strong>₹{total.toFixed(2)}</strong></span>
            </div>
          </div>

          {/* UPI QR Code */}
          {billSettings.showQRCode && qrCodeUrl && (
            <div className="upi-qr-section">
              <h4>UPI Payment QR Code</h4>
              <img src={qrCodeUrl} alt="UPI QR Code" className="qr-code" />
              <p className="qr-instruction">Scan to pay ₹{total.toFixed(2)}</p>
            </div>
          )}

          {/* Footer */}
          <div className="bill-footer">
            <p>Thank you for dining with us!</p>
            <p className="footer-note">Please visit again 😊</p>
            {order.waiterName && (
              <p className="served-by">Served by: {order.waiterName}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate total
const calculateTotal = (order, gstCalculation) => {
  if (!order) return 0;
  return gstCalculation?.total || 0;
};

export default BillPrinting;
